import {
    fullDateFullYearFormat,
    fullDateLongFormat,
} from "@common/helpers/date";
import { Entity } from "@common/types/entity";
import { FinancialAccount } from "@common/types/financialAccount";
import { useMemo, useState } from "react";
import { didRequestReachServer } from "@api/apiError";
import { useFinancialAccountUpdateMutation } from "@api/financialAccount.api";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { formatDateOnly } from "@helpers/date";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";

interface Props {
    financialAccount: FinancialAccount;
    entity: Entity;
}

export const AccountStartDate: React.FC<Props> = ({
    financialAccount,
    entity,
}) => {
    const updateAccount = useFinancialAccountUpdateMutation(financialAccount);
    const workspace = useActiveWorkspace();
    const { error } = useToaster();
    const [pendingDate, setPendingDate] = useState<string | null | undefined>(
        undefined,
    );
    const value =
        financialAccount.bookkeepingStartDate ?? entity.bookkeepingStartDate;
    const maxDate = useMemo(() => {
        const accountEndDate = financialAccount.bookkeepingEndDate;
        const entityEndDate = workspace?.bookkeepingEndDate ?? null;
        if (accountEndDate && entityEndDate) {
            return accountEndDate < entityEndDate
                ? accountEndDate
                : entityEndDate;
        }
        return accountEndDate ?? entityEndDate ?? undefined;
    }, [financialAccount.bookkeepingEndDate, workspace?.bookkeepingEndDate]);

    const saveDate = async (bookkeepingStartDate: string | null) => {
        try {
            await updateAccount.mutateAsync({ bookkeepingStartDate });
        } catch (requestError) {
            if (!didRequestReachServer(requestError)) {
                error("Failed to update account start date");
            }
        }
    };

    const handleValueChange = (date: string | undefined) => {
        const nextDate = date ?? null;
        if (nextDate === financialAccount.bookkeepingStartDate) {
            return;
        }
        const nextEffectiveDate = nextDate ?? entity.bookkeepingStartDate;
        if (nextEffectiveDate === value) {
            void saveDate(nextDate);
            return;
        }
        setPendingDate(nextDate);
    };

    const confirmChange = async () => {
        if (pendingDate === undefined) {
            return;
        }
        await saveDate(pendingDate);
        setPendingDate(undefined);
    };

    const currentDateLabel = formatDateOnly(value, fullDateFullYearFormat);
    const effectiveDateLabel = formatDateOnly(
        pendingDate ?? entity.bookkeepingStartDate,
        fullDateFullYearFormat,
    );

    return (
        <>
            <DatePicker
                mode="day"
                variant="panel-input"
                clearable
                showTriggerIcon={false}
                displayFormat={fullDateLongFormat}
                placeholder="Not set"
                value={value}
                minDate={entity.bookkeepingStartDate}
                maxDate={maxDate}
                onValueChange={handleValueChange}
                disabled={updateAccount.isPending}
            />
            <ConfirmationModal
                show={pendingDate !== undefined}
                title="Update account start date?"
                question={
                    <>
                        <p>
                            {currentDateLabel} → {effectiveDateLabel}
                        </p>
                        <p className="mb-0">
                            {pendingDate === null
                                ? "This account falls back to the entity's bookkeeping start date."
                                : `Transactions in this account before ${effectiveDateLabel} are excluded from your books.`}{" "}
                            Journal entries regenerate.
                        </p>
                    </>
                }
                yes="Update"
                no="Cancel"
                onReject={() => setPendingDate(undefined)}
                onHide={() => setPendingDate(undefined)}
                onConfirm={confirmChange}
            />
        </>
    );
};
